import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, DollarSign, Users, FileText, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import axios from 'axios'; // Add this import
import API from '../../../services/interceptor';

const MonthlyAnalyticsDashboard = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [data, setData] = useState({
    overview: null,
    reports: null,
    appointments: null,
    financials: null,
    opd: null,
    comparison: null
  });

  const API_BASE_URL = 'http://localhost:5000/api/analytics';

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  useEffect(() => {
    fetchAllData();
  }, [selectedYear, selectedMonth]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Create query parameters string
      const params = new URLSearchParams({
        year: selectedYear,
        month: selectedMonth,
      }).toString();

      const endpoints = [
        'monthly-overview',
        'monthly-reports',
        'monthly-appointments',
        'monthly-financials',
        'monthly-opd',
        'monthly-comparison'
      ];

      // Create an array of GET request promises
      const requests = endpoints.map(endpoint => API.get(`/api/analytics/${endpoint}?${params}`));
      
      // Await all requests to resolve
      const responses = await Promise.all(requests);

      // Set state from the response data
      // axios wraps the response payload in a 'data' property.
      // Your API also seems to have a 'data' property in its payload.
      setData({
        overview: responses[0].data.data,
        reports: responses[1].data.data,
        appointments: responses[2].data.data,
        financials: responses[3].data.data,
        opd: responses[4].data.data,
        comparison: responses[5].data.data
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon: Icon, trend, trendValue, color }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {trend && (
            <div className="flex items-center mt-2">
              {trend === 'up' ? (
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                {trendValue}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const renderOverview = () => {
    if (!data.overview || !data.comparison) return null;

    const { metrics } = data.overview;
    const { comparison } = data.comparison;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Patient Reports"
            value={metrics.totalPatientReports}
            icon={FileText}
            trend={comparison.reports.change >= 0 ? 'up' : 'down'}
            trendValue={Math.abs(comparison.reports.percentageChange)}
            color="bg-blue-500"
          />
          <MetricCard
            title="Appointments"
            value={metrics.totalAppointments}
            icon={Calendar}
            trend={comparison.appointments.change >= 0 ? 'up' : 'down'}
            trendValue={Math.abs(comparison.appointments.percentageChange)}
            color="bg-green-500"
          />
          <MetricCard
            title="Total Revenue"
            value={`₹${metrics.totalRevenue.toLocaleString()}`}
            icon={DollarSign}
            trend={comparison.revenue.change >= 0 ? 'up' : 'down'}
            trendValue={Math.abs(comparison.revenue.percentageChange)}
            color="bg-purple-500"
          />
          <MetricCard
            title="OPD Forms"
            value={metrics.totalOPDForms}
            icon={Users}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-semibold text-lg">₹{metrics.totalRevenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Paid</span>
                <span className="font-semibold text-lg text-green-600">₹{metrics.totalPaid.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending</span>
                <span className="font-semibold text-lg text-orange-600">₹{metrics.pending.toLocaleString()}</span>
              </div>
              <div className="pt-3 border-t">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all"
                    style={{ width: `${(metrics.totalPaid / metrics.totalRevenue) * 100}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {((metrics.totalPaid / metrics.totalRevenue) * 100).toFixed(1)}% collected
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Month-over-Month Comparison</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Reports</span>
                  <span className={`text-sm font-semibold ${comparison.reports.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {comparison.reports.change >= 0 ? '+' : ''}{comparison.reports.change}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${comparison.reports.change >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(Math.abs(comparison.reports.percentageChange), 100)}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Appointments</span>
                  <span className={`text-sm font-semibold ${comparison.appointments.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {comparison.appointments.change >= 0 ? '+' : ''}{comparison.appointments.change}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${comparison.appointments.change >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(Math.abs(comparison.appointments.percentageChange), 100)}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Revenue</span>
                  <span className={`text-sm font-semibold ${comparison.revenue.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{comparison.revenue.change >= 0 ? '+' : ''}{comparison.revenue.change.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${comparison.revenue.change >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(Math.abs(comparison.revenue.percentageChange), 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReports = () => {
    if (!data.reports) return null;

    const diagnosisData = data.reports.topDiagnoses.map(item => ({
      name: item._id || 'Not specified',
      value: item.count
    }));

    const treatmentData = data.reports.topTreatments.map(item => ({
      name: item._id || 'Not specified',
      value: item.count
    }));

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Total Reports: {data.reports.totalReports}</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Top Diagnoses</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={diagnosisData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {diagnosisData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Top Treatments</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={treatmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderAppointments = () => {
    if (!data.appointments) return null;

    const statusData = data.appointments.statusBreakdown.map(item => ({
      name: item._id,
      value: item.count
    }));

    const dailyData = data.appointments.dailyAppointments.map(item => ({
      day: item._id,
      appointments: item.count
    }));

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Total Appointments: {data.appointments.totalAppointments}</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Daily Appointments</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="appointments" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Top Reasons for Appointments</h3>
          <div className="space-y-2">
            {data.appointments.topReasons.map((reason, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium">{reason._id}</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                  {reason.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFinancials = () => {
    if (!data.financials) return null;

    const billingData = data.financials.topBillingItems.map(item => ({
      name: item._id,
      revenue: item.totalRevenue,
      quantity: item.totalQuantity
    }));

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-sm text-gray-600 mb-2">Total Revenue</h4>
            <p className="text-3xl font-bold text-gray-900">₹{data.financials.revenue.total.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">From {data.financials.revenue.patientCount} patients</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-sm text-gray-600 mb-2">Total Collected</h4>
            <p className="text-3xl font-bold text-green-600">₹{data.financials.payments.totalCollected.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">From {data.financials.payments.visitCount} visits</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-sm text-gray-600 mb-2">Pending</h4>
            <p className="text-3xl font-bold text-orange-600">₹{data.financials.payments.totalPending.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">Outstanding balance</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Top Billing Items by Revenue</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={billingData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue (₹)" />
              <Bar dataKey="quantity" fill="#10b981" name="Quantity" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue Statistics</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Average per Patient:</span>
                <span className="font-semibold">₹{data.financials.revenue.average.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Minimum:</span>
                <span className="font-semibold">₹{data.financials.revenue.minimum}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Maximum:</span>
                <span className="font-semibold">₹{data.financials.revenue.maximum}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Collection Rate</h4>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-green-500 h-4 rounded-full flex items-center justify-center text-xs text-white font-semibold"
                  style={{ width: `${(data.financials.payments.totalCollected / data.financials.revenue.total) * 100}%` }}
                >
                  {((data.financials.payments.totalCollected / data.financials.revenue.total) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOPD = () => {
    if (!data.opd) return null;

    const ageData = data.opd.ageDistribution.map(item => ({
      range: item._id === 'Other' ? 'Other' : `${item._id}-${item._id === 0 ? 18 : item._id === 18 ? 30 : item._id === 30 ? 45 : item._id === 45 ? 60 : 100}`,
      count: item.count
    }));

    const dailyData = data.opd.dailyRegistrations.map(item => ({
      day: item._id,
      registrations: item.count
    }));

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Total OPD Forms: {data.opd.totalOPDForms}</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Age Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Daily Registrations</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="registrations" stroke="#8b5cf6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'financials', label: 'Financials', icon: DollarSign },
    { id: 'opd', label: 'OPD', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Monthly Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive insights into your practice</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {months.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="mt-7 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'reports' && renderReports()}
            {activeTab === 'appointments' && renderAppointments()}
            {activeTab === 'financials' && renderFinancials()}
            {activeTab === 'opd' && renderOPD()}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyAnalyticsDashboard;